import { useResource } from '../../lib/useResource';
import { api } from '../../lib/api';
import type { FinanceDay } from '@bbbk/schema';

// Value EARNED per day across a window, for the Daily page's Money chart. The
// window is the SAME one the occupancy chart uses, so switching the dropdown
// never silently changes the period underneath.
//
// `enabled` gates the fetch on BOTH the chart being selected and the viewer
// being allowed to see takings — Finances is admin+owner only, and a helper
// hitting /api/finances/daily gets a 403. Gating here means the request is never
// made rather than made and rejected.
//
// Invalidated by 'bookings' writes: editing a stay's rate or its dates changes
// what its nights were worth, and therefore this chart.
export function useDailyMoney(range: { from: string; to: string }, enabled: boolean) {
  const key = enabled ? `daily-money:${range.from}:${range.to}` : null;
  return useResource<FinanceDay[]>(
    key,
    // `cache: 'reload'` on every fetch, not merely the first. The response is
    // browser-cacheable for 30 seconds and useResource cannot tell a mutation-
    // driven revalidation from a cold load, so an ordinary GET after a rate edit
    // could be served the stale body straight from cache and leave the bars
    // unmoved. The chart is fetched only while it is on screen, so forcing
    // revalidation costs one conditional request.
    () =>
      api<FinanceDay[]>(`/finances/daily?from=${range.from}&to=${range.to}`, {
        cache: 'reload',
      }).then((r) => r.data),
    { invalidatedBy: ['bookings'], enabled },
  );
}
